import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './PatentsPage.css';
import { getUserId, getCurrentUser } from '../utils/auth';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import PatentCard from '../components/PatentCard';
import { patentFiltersService, API } from '../services/api';
import Toast from '../components/Toast';
import TrialExpiredOverlay from '../components/TrialExpiredOverlay';
import { useFilters } from '../context/FiltersContext';
import PatentDetail from '../components/PatentDetail';
import { formatMonthKey } from '../components/MonthRangeSlider';

/* ------------------ helpers ------------------ */
const toUniqueStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  return value
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => String(v))
    .filter((v) => (seen.has(v) ? false : (seen.add(v), true)));
};

const sanitizeOptionValues = (values) => {
  const set = new Set();
  (Array.isArray(values) ? values : [])
    .map((value) => {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    })
    .filter((value) => value.length > 0)
    .forEach((value) => set.add(value));
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
};

const UI_STORAGE_KEYS = {
  appliedFilters: 'patents.appliedFilters'
};

const DENSITY_STORAGE_KEY = 'patents.density';

/* Multi-select list filters */
const LIST_FILTER_KEYS = ['applicants', 'category', 'subsystem', 'part'];

/* Score floor filters ("N or higher"), 0 means inactive */
const SCORE_FILTER_KEYS = ['novelty', 'disruption', 'industrialization'];

const FILTER_LABELS = {
  applicants: 'Applicant',
  category: 'Category',
  subsystem: 'Subsystem',
  part: 'Part',
  novelty: 'Novelty',
  disruption: 'Disruption',
  industrialization: 'Industrialization',
  publication_date: 'Year',
};

const clampScore = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.min(10, Math.max(0, Math.round(parsed)));
};

const isMonthKey = (value) => /^\d{6}$/.test(String(value ?? ''));

/* publication_date is a [fromMonth, toMonth] range of YYYYMM keys, or [] when unset */
const sanitizeMonthRange = (value) => {
  if (!Array.isArray(value) || value.length !== 2) return [];
  const [from, to] = value.map((entry) => String(entry));
  if (!isMonthKey(from) || !isMonthKey(to)) return [];
  return from <= to ? [from, to] : [to, from];
};

const sanitizePatentFilters = (raw = {}) => {
  const base = raw && typeof raw === 'object' ? raw : {};
  const sanitized = {};

  LIST_FILTER_KEYS.forEach((key) => {
    sanitized[key] = toUniqueStringArray(base[key]);
  });
  SCORE_FILTER_KEYS.forEach((key) => {
    sanitized[key] = clampScore(base[key]);
  });
  sanitized.publication_date = sanitizeMonthRange(base.publication_date);

  return sanitized;
};

const areFiltersEmpty = (filters) => {
  if (!filters) return true;
  const sanitized = sanitizePatentFilters(filters);
  return LIST_FILTER_KEYS.every((key) => sanitized[key].length === 0)
    && SCORE_FILTER_KEYS.every((key) => sanitized[key] === 0)
    && sanitized.publication_date.length === 0;
};

const areFiltersEqual = (a, b) => {
  return JSON.stringify(sanitizePatentFilters(a)) === JSON.stringify(sanitizePatentFilters(b));
};

const buildPatentSearchParams = (filters) => {
  const params = new URLSearchParams();
  if (!filters) return params;

  LIST_FILTER_KEYS.forEach((key) => {
    const values = filters[key];
    if (Array.isArray(values) && values.length > 0) values.forEach((v) => params.append(key, v));
  });

  SCORE_FILTER_KEYS.forEach((key) => {
    const value = clampScore(filters[key]);
    if (value > 0) params.set(key, String(value));
  });

  const range = sanitizeMonthRange(filters.publication_date);
  if (range.length === 2) {
    params.set('date_from', range[0]);
    params.set('date_to', range[1]);
  }

  return params;
};

/* ------------------ page ------------------ */
const PatentsPage = ({ viewMode = 'card', onViewModeChange }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const setHeaderInfo = outletContext.setHeaderInfo;
  const { updateFilterOptions: updateContextFilterOptions, updateActiveFilters: updateContextActiveFilters, registerFilterCallbacks } = useFilters();

  const [patents, setPatents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedPatent, setSelectedPatent] = useState(null);
  const [toast, setToast] = useState(null);
  const [cardViewSortConfig, setCardViewSortConfig] = useState({ key: 'publication_date', direction: 'descending' });
  const [density, setDensity] = useState(() => {
    if (typeof window === 'undefined') {
      return 'detailed';
    }
    const stored = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return stored === 'compact' ? 'compact' : 'detailed';
  });

  const filtersWrapperRef = useRef(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [isSavingCurrentFilter, setIsSavingCurrentFilter] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
    }
  }, [density]);

  // url <-> state
  const getFiltersFromUrl = () => {
    const params = new URLSearchParams(location.search);
    const fromUrl = {};

    LIST_FILTER_KEYS.forEach((key) => {
      fromUrl[key] = params.getAll(key);
    });
    SCORE_FILTER_KEYS.forEach((key) => {
      fromUrl[key] = params.get(key);
    });

    const dateFrom = params.get('date_from');
    const dateTo = params.get('date_to');
    fromUrl.publication_date = dateFrom && dateTo ? [dateFrom, dateTo] : [];

    return fromUrl;
  };

  const [filters, setFilters] = useState(() => {
    const fromUrl = sanitizePatentFilters(getFiltersFromUrl());
    if (!areFiltersEmpty(fromUrl)) {
      return fromUrl;
    }

    if (typeof window !== 'undefined') {
      try {
        const stored = window.localStorage.getItem(UI_STORAGE_KEYS.appliedFilters);
        if (stored) {
          const parsed = JSON.parse(stored);
          const sanitizedStored = sanitizePatentFilters(parsed);
          if (!areFiltersEmpty(sanitizedStored)) {
            return sanitizedStored;
          }
        }
      } catch (error) {
        console.error('Unable to read saved filters:', error);
      }
    }

    return fromUrl;
  });
  const filtersRef = useRef(filters);

  const [filterOptions, setFilterOptions] = useState({
    applicants: [],
    category: [],
    subsystem: [],
    part: [],
    publication_month_bounds: { min: null, max: null },
  });

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      if (areFiltersEmpty(filters)) {
        window.localStorage.removeItem(UI_STORAGE_KEYS.appliedFilters);
      } else {
        window.localStorage.setItem(UI_STORAGE_KEYS.appliedFilters, JSON.stringify(filters));
      }
    } catch (error) {
      console.error('Unable to save applied filters:', error);
    }
  }, [filters]);

  const syncFiltersToUrl = useCallback(
    (next) => {
      const params = buildPatentSearchParams(next);
      navigate({ search: params.toString() }, { replace: true });
    },
    [navigate]
  );

  const refreshData = () => setRefreshTrigger((p) => p + 1);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  /* -------- saved filters -------- */
  const loadSavedFilters = useCallback(async () => {
    try {
      const response = await patentFiltersService.getAll();
      if (Array.isArray(response?.filters)) setSavedFilters(response.filters);
      else if (Array.isArray(response)) setSavedFilters(response);
      else setSavedFilters([]);
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
  }, []);
  useEffect(() => { loadSavedFilters(); }, [loadSavedFilters]);

  const updateFilter = useCallback((filterName, value) => {
    setFilters((currentFilters) => {
      const next = sanitizePatentFilters({ ...currentFilters, [filterName]: value });
      syncFiltersToUrl(next);
      return next;
    });
  }, [syncFiltersToUrl]);

  const resetAllFilters = useCallback(() => {
    const empty = sanitizePatentFilters({});
    setFilters(empty);
    syncFiltersToUrl(empty);
  }, [syncFiltersToUrl]);

  const handleSaveCurrentFilters = useCallback(async () => {
    if (isSavingCurrentFilter) return;
    const name = typeof window !== 'undefined' ? window.prompt('Name your filter') : '';
    if (!name || !name.trim()) return;

    setIsSavingCurrentFilter(true);
    try {
      const res = await patentFiltersService.save(name.trim(), filters);
      if (res?.success) {
        showToast('Filter saved', 'success');
        await loadSavedFilters();
      } else showToast('Unable to save this filter', 'error');
    } catch (e) {
      console.error('save filter error:', e);
      if (e?.response?.status === 409) showToast('A filter with this name already exists.', 'error');
      else showToast('Error saving filter.', 'error');
    } finally {
      setIsSavingCurrentFilter(false);
    }
  }, [isSavingCurrentFilter, filters, loadSavedFilters, showToast]);

  // Sync filterOptions to FiltersContext
  useEffect(() => {
    updateContextFilterOptions('patents', filterOptions);
  }, [filterOptions, updateContextFilterOptions]);

  // Sync active filters to FiltersContext
  useEffect(() => {
    updateContextActiveFilters('patents', filters);
  }, [filters, updateContextActiveFilters]);

  // Update header info with results count and active filters
  useEffect(() => {
    if (!setHeaderInfo) return;

    const resultsText = patents.length > 0
      ? `Results 1-${patents.length} of ${patents.length}`
      : 'No results found';

    const activeFiltersList = [];

    // One chip per selected value for list filters
    LIST_FILTER_KEYS.forEach((key) => {
      (filters[key] || []).forEach((value) => {
        activeFiltersList.push({
          key: `${key}-${value}`,
          filterKey: key,
          label: FILTER_LABELS[key],
          value,
        });
      });
    });

    // Score floors render as "Novelty 5 or higher"; removing the chip resets to 0
    SCORE_FILTER_KEYS.forEach((key) => {
      const value = clampScore(filters[key]);
      if (value > 0) {
        activeFiltersList.push({
          key,
          filterKey: key,
          label: FILTER_LABELS[key],
          value: `${value} or higher`,
          resetValue: 0,
        });
      }
    });

    // Date range renders as "Year 09/2025 - 07/2026"
    const monthRange = sanitizeMonthRange(filters.publication_date);
    if (monthRange.length === 2) {
      activeFiltersList.push({
        key: 'publication_date',
        filterKey: 'publication_date',
        label: FILTER_LABELS.publication_date,
        value: `${formatMonthKey(monthRange[0])} - ${formatMonthKey(monthRange[1])}`,
        resetValue: [],
      });
    }

    setHeaderInfo({
      resultsText,
      activeFilters: activeFiltersList
    });

    // Cleanup on unmount
    return () => {
      if (setHeaderInfo) {
        setHeaderInfo({
          resultsText: '',
          activeFilters: []
        });
      }
    };
  }, [patents, filters, setHeaderInfo]);

  // Register filter callbacks in FiltersContext
  useEffect(() => {
    registerFilterCallbacks('patents', {
      onFilterChange: updateFilter,
      onResetAllFilters: resetAllFilters,
      onSaveCurrentFilters: handleSaveCurrentFilters,
      savingCurrentFilter: isSavingCurrentFilter,
    });
  }, [registerFilterCallbacks, updateFilter, resetAllFilters, handleSaveCurrentFilters, isSavingCurrentFilter]);

  const handleApplySavedFilter = (doc) => {
    if (!doc) return;
    const payload = doc.filters && typeof doc.filters === 'object' ? doc.filters : doc;
    const sanitized = sanitizePatentFilters(payload);
    setFilters(sanitized);
    syncFiltersToUrl(sanitized);
    showToast(doc.name ? `Filter ${doc.name} applied` : 'Filter applied', 'success');
  };

  const handleRequestRemoveSavedFilter = async (doc) => {
    if (!doc) return;
    const ok = typeof window !== 'undefined'
      ? window.confirm(`Delete filter "${doc?.name || 'Unnamed'}"?`)
      : true;
    if (!ok) return;
    try {
      await patentFiltersService.remove(doc._id || doc.id);
      showToast('Filter removed', 'success');
      await loadSavedFilters();
    } catch (e) {
      console.error('remove filter error:', e);
      showToast('Unable to remove this filter.', 'error');
    }
  };

  const renderSavedFiltersPanel = () => {
    return (
      <div className="patents-saved-filters-panel">
        <div className="patents-saved-filters-panel__header">
          {Array.isArray(savedFilters) && savedFilters.length > 0 && (
            <>
              <span className="patents-saved-filters-panel__title">{`Saved Filters${savedFilters.length ? ` (${savedFilters.length})` : ''}:`}</span>
              <div className="patents-saved-filters-panel__list">
                {savedFilters.map((doc) => {
                  const key = doc?._id || doc?.id || doc?.name;
                  if (!key) return null;
                  const name = doc?.name || 'Sans nom';
                  const onKey = (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleApplySavedFilter(doc);
                    }
                  };
                  return (
                    <span
                      key={key}
                      className="patents-filter-chip patents-saved-filter-chip"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleApplySavedFilter(doc)}
                      onKeyDown={onKey}
                    >
                      <span className="patents-filter-chip-label">Filter</span>
                      <span className="patents-filter-chip-value">{name}</span>
                      <button
                        type="button"
                        className="filter-chip-remove patents-saved-filter-chip__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestRemoveSavedFilter(doc);
                        }}
                        aria-label={`Supprimer le filtre ${name}`}
                      >
                        &times;
                      </button>
                    </span>
                  );
                })}
              </div>
            </>
          )}

          <div className="patents-saved-filters-panel__actions">
            <div className="patents-sort">
              <label htmlFor="sort-select" className="patents-sort-label">Sort by</label>
              <select
                id="sort-select"
                className="patents-sort-select"
                value={`${cardViewSortConfig.key}:${cardViewSortConfig.direction}`}
                onChange={handleCardViewSortChange}
              >
                <option value="publication_date:descending">Most recent</option>
                <option value="publication_date:ascending">Oldest</option>
                <option value="title:ascending">Title A-Z</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* -------- filter options (stats) -------- */
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await API.get('/patents/stats');
        if (response.data?.success) {
          const av = response.data.availableValues || {};
          const bounds = av.publication_month_bounds || {};
          setFilterOptions({
            applicants: sanitizeOptionValues(av.applicants),
            category: sanitizeOptionValues(av.category),
            subsystem: sanitizeOptionValues(av.subsystem),
            part: sanitizeOptionValues(av.part),
            publication_month_bounds: {
              min: isMonthKey(bounds.min) ? String(bounds.min) : null,
              max: isMonthKey(bounds.max) ? String(bounds.max) : null,
            },
          });
        }
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // sync when URL changes
  useEffect(() => {
    const fromUrl = sanitizePatentFilters(getFiltersFromUrl());
    const currentFilters = filtersRef.current;

    if (location.search.length === 0 && !areFiltersEmpty(currentFilters)) {
      return;
    }

    if (!areFiltersEqual(currentFilters, fromUrl)) {
      setFilters(fromUrl);
    }
  }, [location.search]);

  /* -------- patents -------- */
  useEffect(() => {
    const fetchPatents = async () => {
      setIsLoading(true);
      try {
        const params = {};

        LIST_FILTER_KEYS.forEach((key) => {
          if (filters[key]?.length) params[key] = filters[key];
        });

        SCORE_FILTER_KEYS.forEach((key) => {
          const value = clampScore(filters[key]);
          if (value > 0) params[`${key}_min`] = value;
        });

        const monthRange = sanitizeMonthRange(filters.publication_date);
        if (monthRange.length === 2) {
          params.date_from = monthRange[0];
          params.date_to = monthRange[1];
        }

        const userId = getUserId();
        if (userId) params.userId = userId;
        params._t = Date.now();

        console.log('Fetching patents with params:', params);
        const response = await API.get('/patents', { params });
        if (response.data?.success) {
          const patentsPayload = Array.isArray(response.data.patents) ? response.data.patents : [];
          setPatents(patentsPayload);
          setError(null);
        } else setError('Error loading patents');
      } catch (err) {
        console.error('Error loading patents:', err);
        setError('Server connection error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPatents();
  }, [filters, refreshTrigger]);

  const handleCardViewSortChange = (e) => {
    const [key, direction] = e.target.value.split(':');
    setCardViewSortConfig({ key, direction });
  };

  const sortedPatents = useMemo(() => {
    return [...patents].sort((a, b) => {
      const { key, direction } = cardViewSortConfig;
      let aVal = key === 'title' ? (a.title_en || a.title || '') : (a[key] || '');
      let bVal = key === 'title' ? (b.title_en || b.title || '') : (b[key] || '');

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (direction === 'ascending') return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    });
  }, [patents, cardViewSortConfig]);

  /* ------------------ render ------------------ */
  return (
    <div className="patents-page">
      <div className="patents-layout">
        <div className="patents-sticky-container">
          {renderSavedFiltersPanel()}
        </div>

        <section className="patents-results-section" style={{ position: 'relative' }}>
          <TrialExpiredOverlay section="results" />
          {isLoading ? (
            <div className="loading-indicator">Loading patents...</div>
          ) : error ? (
            <div className="error-message">
              Error: {error} <button onClick={refreshData}>Retry</button>
            </div>
          ) : (
            <>
              <div className={`card-grid ${sortedPatents.length <= 2 ? 'card-grid-limited' : ''}`}>
                {sortedPatents.length === 0 ? (
                  <div className="no-results">No results found</div>
                ) : (
                  sortedPatents.map((patent) => (
                    <PatentCard
                      key={patent._id || patent.id}
                      patent={patent}
                      onClick={() => setSelectedPatent(patent)}
                    />
                  ))
                )}
              </div>

              {selectedPatent && (
                <PatentDetail
                  patent={selectedPatent}
                  onClose={() => setSelectedPatent(null)}
                />
              )}
            </>
          )}
          {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        </section>
      </div>
    </div>
  );
};

export default PatentsPage;
